import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
  Tooltip,
  makeStyles,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { isUrlLive } from "../../helpers/url";
import { useCopyToClipboard } from "react-use";

/**
 * PTR-99: topics that a Direct API project can never deliver to a partner.
 *
 * webhook-server-v2 MainController.ts:1811 branches on `assistant.directApikey`
 * and forwards the event to that project's own Direct API webhook INSTEAD of
 * running the partner-notification block that sits in the else. Subscribing to
 * the topic still succeeds, and nothing is logged, so the partner sees silence
 * and raises a ticket. Say so at the point of subscription.
 *
 * There is deliberately no automatic hiding here: `directApikey` is a PROJECT
 * field (assistant.model.ts), not a partner one, and a partner can hold a mix of
 * Direct API and normal projects - so the checkbox stays usable and only carries
 * the caveat.
 */
const DIRECT_API_UNDELIVERED_TOPICS = ["wa_template.status.updated"];

const DIRECT_API_TOPIC_NOTE =
  "Not delivered for Direct API projects - their template events are forwarded " +
  "to the project's own Direct API webhook instead of the partner webhook.";

const WEBHOOKS_TOPICS = [
  "business.created",
  "business.deleted",
  "business.updated",
  "project.created",
  "project.deleted",
  "project.status.updated",
  "project.waba.updated",
  "project.plan.updated",
  "wa_template.status.updated",
  "project-wc-created",
  "project-payment-captured",
  "ad.profile.connected",
  "ad.publishing.entity.selected",
  "ad.setup.completed",
  "ad.created",
];

const useStyles = makeStyles({
  root: {
    padding: "2rem 1rem 1rem 1rem",
    backgroundColor: "#EBF5F3",
    borderRadius: "12px",
  },
  textField: {
    backgroundColor: "white",
    border: "1px solid black",
  },
  textFieldDisabled: {
    backgroundColor: "white",
  },
});

/**
 * @typedef {import("../../types").Partner} Partner
 */

/**
 * @typedef {Object} PartnerCredentialsInputProps
 * @property {Partner} partner
 * @property {() => Promise<string | undefined>} resetPassword
 * @property {() => Promise<any | undefined>} resetApiKey
 * @property {(webhookUrl: string) => Promise<any | undefined>} updateSharedSecret
 */

/**
 * @param {PartnerCredentialsInputProps} props
 */
function PartnerCredentialsInput({
  partner,
  resetPassword,
  resetApiKey,
  updateSharedSecret,
  handleWebhookTopic,
  showSuccessMessage,
  showErrorMessage,
  agent,
  hasWriteAccess,
}) {
  const classes = useStyles();
  const [_, copyToClipboard] = useCopyToClipboard();

  const [newApiKey, setNewApiKey] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newWebhookUrl, setNewWebhookUrl] = useState(partner.webhookUrl);
  const [isWebhookVerified, setIsWebhookVerified] = useState(false);
  const [sharedSecret, setSharedSecret] = useState(partner.sharedSecret);
  const [webhooks, setWebhooks] = useState(partner.partnerWebhooks || []);

  async function handleVerifyWebhook() {
    const isLive = await isUrlLive(newWebhookUrl);
    if (isLive && webhooks?.length > 0) {
      setIsWebhookVerified(true);
      showSuccessMessage("Webhook Subscription Verified!");
    } else {
      setIsWebhookVerified(false);
      if (!isLive) {
        showErrorMessage("Webhook URL is not valid");
      } else {
        showErrorMessage("Please select at least one webhook topic");
      }
      console.log("Webhook URL is not valid");
    }
  }

  async function handleResetPassword() {
    const newPass = await resetPassword();
    newPass && setNewPassword(newPass);
  }

  async function handleResetApiKey() {
    const newKey = await resetApiKey();
    newKey && setNewApiKey(newKey.apiKey);
  }

  async function handleUpdateWebhookSecret() {
    const data = await updateSharedSecret();
    data && setSharedSecret(data.sharedSecret);
  }

  async function handleCheckboxChange(topic) {
    if (webhooks.includes(topic)) {
      setWebhooks(webhooks.filter((item) => item !== topic));
    } else {
      setWebhooks([...webhooks, topic]);
    }
  }

  async function handleUpdateWebhook() {
    const data = await handleWebhookTopic(newWebhookUrl, webhooks);
    if (data) {
      setNewWebhookUrl(data.webhookUrl);
      setWebhooks(data.partnerWebhooks);
    }
  }

  return (
    <fieldset
      disabled={!hasWriteAccess}
      style={{ border: "none", padding: 0, margin: 0 }}
    >
      <Box className={classes.root}>
        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Partner Login
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              className={classes.textFieldDisabled}
              disabled={true}
              type="text"
              fullWidth
              value={partner.loginUsername}
            />
          </Grid>
        </Grid>
        {/** Partner Password */}
        <Box my={6}></Box>
        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Partner Login Password
        </Typography>
        <Typography variant="body2" gutterBottom>
          ** Password shown here will lost after page refresh **
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              className={classes.textFieldDisabled}
              disabled={true}
              type="text"
              placeholder="Click Reset to get a new password"
              fullWidth
              value={newPassword}
              InputProps={{
                endAdornment: (
                  <span>
                    <Tooltip title="Click to copy Password">
                      <IconButton
                        size="small"
                        disabled={!newPassword}
                        onClick={() => copyToClipboard(newPassword)}
                      >
                        <FileCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </span>
                ),
              }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleResetPassword}
            >
              Reset
            </Button>
          </Grid>
        </Grid>
        {/** Parter API Key */}
        <Box my={6}></Box>
        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Partner API Key (Generated key will be displayed here after clicking
          Regenerate)
        </Typography>
        <Typography variant="body2" gutterBottom>
          ** API Key shown here will lost after page refresh **
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={6}>
            <TextField
              className={classes.textFieldDisabled}
              disabled={true}
              type="text"
              placeholder="Click Regenerate to get a new key"
              fullWidth
              value={newApiKey}
              InputProps={{
                endAdornment: (
                  <Tooltip title="Click to copy API Key">
                    <IconButton
                      size="small"
                      disabled={!newApiKey}
                      onClick={() => copyToClipboard(newApiKey)}
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleResetApiKey}
            >
              Regenerate
            </Button>
          </Grid>
        </Grid>
        {/** Parter Webhook URL */}
        <Box my={6}></Box>
        <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
          Partner Webhooks
        </Typography>

        <Grid
          container
          spacing={2}
          alignItems="center"
          style={{ marginBottom: "10px" }}
        >
          <Grid item xs={6}>
            <TextField
              type="text"
              fullWidth
              value={sharedSecret}
              className={classes.textFieldDisabled}
              placeholder="Webhook Secret"
              InputProps={{
                endAdornment: (
                  <Tooltip title="Click to copy Webhook secret">
                    <IconButton
                      size="small"
                      disabled={!sharedSecret}
                      onClick={() => {
                        navigator.clipboard.writeText(sharedSecret);
                      }}
                    >
                      <FileCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              }}
            />
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              disabled={false}
              onClick={handleUpdateWebhookSecret}
            >
              Regenerate webhook secret
            </Button>
          </Grid>
        </Grid>

        <Box
          border={1}
          borderColor="grey.500"
          borderRadius="borderRadius"
          p={2}
          style={{ backgroundColor: "rgb(240, 240, 240)" }}
        >
          {/* <Typography
          variant="body2"
          gutterBottom
          style={{
            fontFamily: "monospace",
            marginBottom: "20px",
          }}
        >
          Events: {partner?.partnerWebhooks?.join(", ")}
        </Typography> */}
          <Grid
            container
            spacing={2}
            alignItems="center"
            style={{ marginTop: "1px" }}
          >
            <Box>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={6}>
                  <TextField
                    type="url"
                    value={newWebhookUrl}
                    onChange={(e) => setNewWebhookUrl(e.target.value)}
                    className={classes.textField}
                    disabled={isWebhookVerified}
                    fullWidth
                    style={{ marginLeft: "1%", width: "99%" }}
                    InputProps={{
                      endAdornment: (
                        <Box mr={-2}>
                          <Button
                            size="small"
                            color={isWebhookVerified ? "default" : "primary"}
                            onClick={
                              !isWebhookVerified
                                ? handleVerifyWebhook
                                : () => setIsWebhookVerified(false)
                            }
                          >
                            {!isWebhookVerified ? "Verify" : "Modify"}
                          </Button>
                        </Box>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!isWebhookVerified}
                    onClick={handleUpdateWebhook}
                  >
                    Update Webhook
                  </Button>
                </Grid>
              </Grid>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "space-between",
                  paddingLeft: "4%",
                  paddingTop: "2%",
                }}
              >
                {WEBHOOKS_TOPICS.map((topic) => {
                  const isDirectApiUndelivered =
                    DIRECT_API_UNDELIVERED_TOPICS.includes(topic);
                  const control = (
                    <FormControlLabel
                      key={topic}
                      control={
                        <Checkbox
                          checked={webhooks?.includes(topic)}
                          onChange={() => handleCheckboxChange(topic)}
                          color="primary"
                        />
                      }
                      label={
                        isDirectApiUndelivered ? (
                          <span>
                            {topic}
                            <Typography
                              variant="caption"
                              display="block"
                              style={{ color: "#B45309", lineHeight: 1.2 }}
                            >
                              not sent for Direct API projects
                            </Typography>
                          </span>
                        ) : (
                          topic
                        )
                      }
                      disabled={isWebhookVerified}
                      style={{
                        width: "30%",
                        marginBottom: "5px",
                      }}
                    />
                  );
                  return isDirectApiUndelivered ? (
                    <Tooltip key={topic} title={DIRECT_API_TOPIC_NOTE}>
                      {control}
                    </Tooltip>
                  ) : (
                    control
                  );
                })}
              </div>
            </Box>
          </Grid>
        </Box>
      </Box>
    </fieldset>
  );
}
const connectedPartnerCredentialsInput = connect((state) => ({
  agent: state.login.user,
}))(PartnerCredentialsInput);

export default connectedPartnerCredentialsInput;
